import {
    DomainClasses,
    Mapper,
    IGoalDTO,
    IPartnerDTO,
    IServiceDTO,
    IToolDTO,
    IProfileTextDTO,
    IPackageDTO,
} from '@raskrask-dk/bookingflow-state-machine'
import { t } from 'i18next'
import {
    GoalBF,
    numToRating,
    PackageBF,
    PartnerBF,
    ReviewBF,
    ServiceBF,
} from '.'
import { Goal, Service, PackageTemplate } from '@raskrask-dk/raskrask-utils'

const getIconByToolId = (toolId: number) => {
    switch (toolId) {
        case 1:
            return 'toolId_1_bliv_afstresset_ikon'
        case 2:
            return 'toolId_2_tag_udgangspunkt_i_dit_flow_ikon'
        case 3:
            return 'toolId_3_knee_pain_ikon'
        case 4:
            return 'toolId_4_teknik_styrke_ikon'
        case 5:
            return 'toolId_5_mindfullness_ikon'
        case 6:
            return 'toolId_6_faa_kropsbevidsthed_ikon'
        case 7:
            return 'toolId_7_dokument_ikon'
        case 8:
            return 'toolId_8_Kostvejledning_ikon_1'
        case 9:
            return 'toolId_9_livsstilscoaching_ikon_1'
        case 10:
            return 'toolId_10_teknik_styrke_ikon'
        case 11:
            return 'toolId_11_udstyr_ikon'
        case 12:
            return 'toolId_12_teknik_styrke_ikon'
        case 13:
            return 'toolId_13_coaching_ikon_2'
        case 14:
            return 'toolId_14_teknik_kondi_ikon'
        default:
            throw new Error('Not implemented yet.')
    }
}

const getTitleByToolId = (toolId: number) => {
    return t(`toolTitle.${toolId}`)
}

const partnerToolsAndProfilTextsPerGoal = (
    tools: IToolDTO[],
    profileText: IProfileTextDTO[]
) => {
    const goalIds = Array.from(
        new Set([
            ...tools.map((tool) => tool.goalId),
            ...profileText.map((pt) => pt.goalId),
        ])
    )
    const texts = new Map()
    goalIds.forEach((goalId) => {
        const toolsOfGoal = tools
            .filter((tool) => tool.goalId === goalId)
            .map((tool) => {
                return {
                    name: getTitleByToolId(tool.toolId),
                    picture: getIconByToolId(tool.toolId),
                    text: tool.text,
                }
            })
        const profileTextsOfGoal = profileText
            .filter((pf) => pf.goalId === goalId)
            .map((pt) => pt.text)
        texts.set(goalId, {
            profileText: profileTextsOfGoal,
            tools: toolsOfGoal,
        })
    })
    return texts
}

const reviewsAverage = (reviews: ReviewBF[]) =>
    reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length

const domainMapper: DomainClasses = {
    goal: (dto: IGoalDTO): GoalBF => {
        const goal = new Goal(dto.id, dto.name)
        return new GoalBF(goal, {})
    },

    service: (dto: IServiceDTO): ServiceBF => {
        const { id, name, duration, isFreeConsultation } = dto

        const bfConstructor = { text: '', icon: '' }

        const service = new Service(id, name, duration, isFreeConsultation)
        return new ServiceBF(service, bfConstructor)
    },

    partner: (dto: IPartnerDTO): PartnerBF => {
        const {
            id,
            name,
            education,
            profileTexts,
            reviews: reviewDtos,
            gdprLink,
            tools,
            profilePictureLink,
        } = dto

        const reviews: ReviewBF[] = reviewDtos.map((reviewDto) => {
            const { date, rating: ratingDto, text, reviewerName } = reviewDto
            const comment = text
            const customer = reviewerName
            const rating = numToRating(ratingDto.toString())
            return new ReviewBF(comment, date, customer, rating)
        })

        const average = reviewsAverage(reviews)
        const texts = partnerToolsAndProfilTextsPerGoal(tools, profileTexts)
        const partner = new PartnerBF(
            id,
            name,
            education,
            gdprLink,
            reviews,
            texts,
            profilePictureLink,
            average
        )
        return partner
    },

    packageTemplate: (dto: IPackageDTO, service: Service): PackageBF => {
        const { id, name, multiplier, discount } = dto

        const packageTemplate = new PackageTemplate(
            service,
            id,
            name,
            multiplier,
            discount
        )
        return PackageBF.fromPackageTemplate(packageTemplate)
    },
}

export const MapperBF = new Mapper(domainMapper)
